import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useGlobalAction, useUser } from "@gadgetinc/react";
import { api } from "../api";
import { hasAnyRole, isInstitutionAdministrator } from "../lib/roleUtils";

function checkIsAdmin(user) {
  if (!user) return false;
  if (user?.email === "74spartan@gmail.com") return true;
  const userRoles = user?.roles || user?.roleList || [];
  const hasAdminRole = hasAnyRole(userRoles, ["admin", "system-admin", "sysadmin"]);
  return hasAdminRole || isInstitutionAdministrator(user);
}

const cardStyle = {
  background: "#1f1f1f",
  border: "1px solid #333",
  borderRadius: "12px",
  padding: "1rem",
};

const buttonStyle = {
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0.55rem 0.85rem",
  cursor: "pointer",
  fontWeight: 600,
};

export default function AdminRepairTools() {
  const user = useUser(api);
  const navigate = useNavigate();
  const isAdmin = checkIsAdmin(user);

  const [results, setResults] = useState({});
  const [errors, setErrors] = useState({});

  const [repairDryRun, setRepairDryRun] = useState(true);
  const [repairLimit, setRepairLimit] = useState(500);
  const [repairEmail, setRepairEmail] = useState("");

  const [backfillDryRun, setBackfillDryRun] = useState(true);
  const [recalcDryRun, setRecalcDryRun] = useState(true);
  const [recalcCohortId, setRecalcCohortId] = useState("");

  const [{ fetching: repairing }, runRepair] = useGlobalAction(api.repairInvitedUserCohortMemberships);
  const [{ fetching: populatingEmails }, runPopulateEmails] = useGlobalAction(api.populateCohortMembershipEmails);
  const [{ fetching: backfillingShiftTypes }, runBackfillShiftTypes] = useGlobalAction(api.backfillShiftTypes);
  const [{ fetching: recalculating }, runRecalculateCounts] = useGlobalAction(api.recalculateShiftCounts);
  const [{ fetching: enablingPrintSave }, runEnablePrintSave] = useGlobalAction(api.updateAllUsersWithPrintSavePermission);
  const [{ fetching: syncingInstructors }, runSyncInstructors] = useGlobalAction(api.syncCohortInstructorsToMemberships);
  const [{ fetching: backfillingDebriefFlags }, runBackfillDebriefFlags] = useGlobalAction(api.backfillDebriefAudioFlags);

  React.useEffect(() => {
    if (user && !isAdmin) navigate("/sign-in");
  }, [user, isAdmin, navigate]);

  const runTool = async (key, fn, params = {}) => {
    setErrors((prev) => ({ ...prev, [key]: null }));
    try {
      const result = await fn(params);
      setResults((prev) => ({ ...prev, [key]: result }));
    } catch (error) {
      setErrors((prev) => ({ ...prev, [key]: error?.message || "Action failed" }));
    }
  };

  if (!user) {
    return (
      <div style={{ minHeight: "100vh", background: "#111", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading...
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ minHeight: "100vh", background: "#111", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
        Access denied.
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#111", color: "#fff", padding: "1.5rem", maxWidth: "1200px", margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
        <h1 style={{ margin: 0, fontSize: "1.6rem" }}>Admin Repair Tools</h1>
        <Link to="/admin" style={{ color: "#93c5fd", textDecoration: "none" }}>
          ← Back to Admin
        </Link>
      </div>

      <p style={{ color: "#d1d5db", marginBottom: "1.25rem" }}>
        Run one-time maintenance and backfill actions from an authenticated admin session.
      </p>

      <div style={{ display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))" }}>
        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Repair Invited User Cohort Memberships</h3>
          <p style={{ color: "#cbd5e1" }}>Backfill/reactivate `cohortMembership` from `invitationData.cohortId`.</p>
          <div style={{ display: "flex", gap: "0.75rem", marginBottom: "0.75rem" }}>
            <label>
              <input type="checkbox" checked={repairDryRun} onChange={(e) => setRepairDryRun(e.target.checked)} /> Dry run
            </label>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
            <input
              type="number"
              min="1"
              max="2000"
              value={repairLimit}
              onChange={(e) => setRepairLimit(Number(e.target.value || 500))}
              placeholder="limit"
              style={{ flex: 1, background: "#0f172a", border: "1px solid #334155", color: "#fff", borderRadius: "8px", padding: "0.45rem 0.6rem" }}
            />
            <input
              type="text"
              value={repairEmail}
              onChange={(e) => setRepairEmail(e.target.value)}
              placeholder="optional email"
              style={{ flex: 2, background: "#0f172a", border: "1px solid #334155", color: "#fff", borderRadius: "8px", padding: "0.45rem 0.6rem" }}
            />
          </div>
          <button
            style={buttonStyle}
            disabled={repairing}
            onClick={() =>
              runTool("repairInvitedUserCohortMemberships", runRepair, {
                dryRun: repairDryRun,
                limit: repairLimit,
                ...(repairEmail.trim() ? { email: repairEmail.trim().toLowerCase() } : {}),
              })
            }
          >
            {repairing ? "Running..." : "Run Repair"}
          </button>
          {errors.repairInvitedUserCohortMemberships && <p style={{ color: "#f87171" }}>{errors.repairInvitedUserCohortMemberships}</p>}
          {results.repairInvitedUserCohortMemberships && (
            <pre style={{ marginTop: "0.75rem", maxHeight: "220px", overflow: "auto", background: "#020617", border: "1px solid #1e293b", padding: "0.6rem", borderRadius: "8px" }}>
              {JSON.stringify(results.repairInvitedUserCohortMemberships, null, 2)}
            </pre>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Populate Cohort Membership Emails</h3>
          <label>
            <input type="checkbox" checked={backfillDryRun} onChange={(e) => setBackfillDryRun(e.target.checked)} /> Dry run
          </label>
          <div style={{ marginTop: "0.75rem" }}>
            <button
              style={buttonStyle}
              disabled={populatingEmails}
              onClick={() => runTool("populateCohortMembershipEmails", runPopulateEmails, { dryRun: backfillDryRun })}
            >
              {populatingEmails ? "Running..." : "Run"}
            </button>
          </div>
          {errors.populateCohortMembershipEmails && <p style={{ color: "#f87171" }}>{errors.populateCohortMembershipEmails}</p>}
          {results.populateCohortMembershipEmails && (
            <pre style={{ marginTop: "0.75rem", maxHeight: "220px", overflow: "auto", background: "#020617", border: "1px solid #1e293b", padding: "0.6rem", borderRadius: "8px" }}>
              {JSON.stringify(results.populateCohortMembershipEmails, null, 2)}
            </pre>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Backfill Shift Types</h3>
          <label>
            <input type="checkbox" checked={backfillDryRun} onChange={(e) => setBackfillDryRun(e.target.checked)} /> Dry run
          </label>
          <div style={{ marginTop: "0.75rem" }}>
            <button
              style={buttonStyle}
              disabled={backfillingShiftTypes}
              onClick={() => runTool("backfillShiftTypes", runBackfillShiftTypes, { dryRun: backfillDryRun, batchSize: 100 })}
            >
              {backfillingShiftTypes ? "Running..." : "Run"}
            </button>
          </div>
          {errors.backfillShiftTypes && <p style={{ color: "#f87171" }}>{errors.backfillShiftTypes}</p>}
          {results.backfillShiftTypes && (
            <pre style={{ marginTop: "0.75rem", maxHeight: "220px", overflow: "auto", background: "#020617", border: "1px solid #1e293b", padding: "0.6rem", borderRadius: "8px" }}>
              {JSON.stringify(results.backfillShiftTypes, null, 2)}
            </pre>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Recalculate Shift Counts</h3>
          <label>
            <input type="checkbox" checked={recalcDryRun} onChange={(e) => setRecalcDryRun(e.target.checked)} /> Dry run
          </label>
          <div style={{ marginTop: "0.75rem", marginBottom: "0.75rem" }}>
            <input
              type="text"
              value={recalcCohortId}
              onChange={(e) => setRecalcCohortId(e.target.value)}
              placeholder="optional cohortId"
              style={{ width: "100%", background: "#0f172a", border: "1px solid #334155", color: "#fff", borderRadius: "8px", padding: "0.45rem 0.6rem" }}
            />
          </div>
          <button
            style={buttonStyle}
            disabled={recalculating}
            onClick={() =>
              runTool("recalculateShiftCounts", runRecalculateCounts, {
                dryRun: recalcDryRun,
                ...(recalcCohortId.trim() ? { cohortId: recalcCohortId.trim() } : {}),
              })
            }
          >
            {recalculating ? "Running..." : "Run"}
          </button>
          {errors.recalculateShiftCounts && <p style={{ color: "#f87171" }}>{errors.recalculateShiftCounts}</p>}
          {results.recalculateShiftCounts && (
            <pre style={{ marginTop: "0.75rem", maxHeight: "220px", overflow: "auto", background: "#020617", border: "1px solid #1e293b", padding: "0.6rem", borderRadius: "8px" }}>
              {JSON.stringify(results.recalculateShiftCounts, null, 2)}
            </pre>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Sync Instructors to Membership Access</h3>
          <p style={{ color: "#cbd5e1" }}>Sets `hasFisdapReviewAccess=true` for instructor memberships.</p>
          <button
            style={buttonStyle}
            disabled={syncingInstructors}
            onClick={() => runTool("syncCohortInstructorsToMemberships", runSyncInstructors)}
          >
            {syncingInstructors ? "Running..." : "Run"}
          </button>
          {errors.syncCohortInstructorsToMemberships && <p style={{ color: "#f87171" }}>{errors.syncCohortInstructorsToMemberships}</p>}
          {results.syncCohortInstructorsToMemberships && (
            <pre style={{ marginTop: "0.75rem", maxHeight: "220px", overflow: "auto", background: "#020617", border: "1px solid #1e293b", padding: "0.6rem", borderRadius: "8px" }}>
              {JSON.stringify(results.syncCohortInstructorsToMemberships, null, 2)}
            </pre>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Backfill Debrief Audio Flags</h3>
          <label>
            <input type="checkbox" checked={backfillDryRun} onChange={(e) => setBackfillDryRun(e.target.checked)} /> Dry run
          </label>
          <div style={{ marginTop: "0.75rem" }}>
            <button
              style={buttonStyle}
              disabled={backfillingDebriefFlags}
              onClick={() => runTool("backfillDebriefAudioFlags", runBackfillDebriefFlags, { dryRun: backfillDryRun })}
            >
              {backfillingDebriefFlags ? "Running..." : "Run"}
            </button>
          </div>
          {errors.backfillDebriefAudioFlags && <p style={{ color: "#f87171" }}>{errors.backfillDebriefAudioFlags}</p>}
          {results.backfillDebriefAudioFlags && (
            <pre style={{ marginTop: "0.75rem", maxHeight: "220px", overflow: "auto", background: "#020617", border: "1px solid #1e293b", padding: "0.6rem", borderRadius: "8px" }}>
              {JSON.stringify(results.backfillDebriefAudioFlags, null, 2)}
            </pre>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={{ marginTop: 0 }}>Enable Print/Save Chats for All Users</h3>
          <p style={{ color: "#cbd5e1" }}>Sets `canPrintSaveChats=true` globally.</p>
          <button
            style={{ ...buttonStyle, background: "#b91c1c" }}
            disabled={enablingPrintSave}
            onClick={() => runTool("updateAllUsersWithPrintSavePermission", runEnablePrintSave)}
          >
            {enablingPrintSave ? "Running..." : "Run (Writes)"}
          </button>
          {errors.updateAllUsersWithPrintSavePermission && <p style={{ color: "#f87171" }}>{errors.updateAllUsersWithPrintSavePermission}</p>}
          {results.updateAllUsersWithPrintSavePermission && (
            <pre style={{ marginTop: "0.75rem", maxHeight: "220px", overflow: "auto", background: "#020617", border: "1px solid #1e293b", padding: "0.6rem", borderRadius: "8px" }}>
              {JSON.stringify(results.updateAllUsersWithPrintSavePermission, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
